import { doc, getDoc, setDoc, collection, addDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

export const cartService = {

    async getCart(userId) {
        try {
            const cartRef = doc(db, 'cart', userId);
            const cartSnap = await getDoc(cartRef);

            if (cartSnap.exists()) {
                return { data: cartSnap.data(), error: null };
            } else {
                const emptyCart = {
                    items: [],
                    total: 0,
                    updatedAt: new Date().toISOString()
                };
                await setDoc(cartRef, emptyCart);
                return { data: emptyCart, error: null };
            }
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    async addToCart(userId, item) {
        try {
            const { data: currentCart } = await this.getCart(userId);
            if (!currentCart) throw new Error('Could not get cart');

            const existingItemIndex = currentCart.items.findIndex(
                cartItem => cartItem.id === item.id && cartItem.type === item.type
            );

            let updatedItems;
            if (existingItemIndex >= 0) {
                updatedItems = currentCart.items.map((cartItem, index) =>
                    index === existingItemIndex
                        ? { ...cartItem, quantity: cartItem.quantity + item.quantity }
                        : cartItem
                );
            } else {
                updatedItems = [...currentCart.items, { ...item, addedAt: new Date().toISOString() }];
            }

            const total = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

            const updatedCart = {
                items: updatedItems,
                total: total,
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'cart', userId), updatedCart);
            toast.success('Added to cart!');
            return { data: updatedCart, error: null };
        } catch (error) {
            toast.error('Error adding to cart');
            return { data: null, error: error.message };
        }
    },

    async updateCartItem(userId, itemId, quantity) {
        try {
            const { data: currentCart } = await this.getCart(userId);
            if (!currentCart) throw new Error('Could not get cart');

            let updatedItems;
            if (quantity <= 0) {
                updatedItems = currentCart.items.filter(item => item.id !== itemId);
            } else {
                updatedItems = currentCart.items.map(item =>
                    item.id === itemId ? { ...item, quantity } : item
                );
            }

            const total = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

            const updatedCart = {
                items: updatedItems,
                total: total,
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'cart', userId), updatedCart);
            return { data: updatedCart, error: null };
        } catch (error) {
            toast.error('Error updating cart');
            return { data: null, error: error.message };
        }
    },

    async removeFromCart(userId, itemId) {
        try {
            const { data: currentCart } = await this.getCart(userId);
            if (!currentCart) throw new Error('Could not get cart');

            const updatedItems = currentCart.items.filter(item => item.id !== itemId);
            const total = updatedItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);

            const updatedCart = {
                items: updatedItems,
                total: total,
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'cart', userId), updatedCart);
            toast.success('Item removed from cart');
            return { data: updatedCart, error: null };
        } catch (error) {
            toast.error('Error removing item');
            return { data: null, error: error.message };
        }
    },

    async clearCart(userId) {
        try {
            const emptyCart = {
                items: [],
                total: 0,
                updatedAt: new Date().toISOString()
            };

            await setDoc(doc(db, 'cart', userId), emptyCart);
            return { data: emptyCart, error: null };
        } catch (error) {
            return { data: null, error: error.message };
        }
    },

    validateCartForCheckout(cart) {
        if (!cart || !cart.items || cart.items.length === 0) {
            return { valid: false, error: 'Cart is empty' };
        }

        for (const item of cart.items) {
            if (!item.id || !item.name || !item.price || !item.quantity) {
                return { valid: false, error: 'Invalid item in cart' };
            }
            if (item.quantity <= 0) {
                return { valid: false, error: 'Item quantity must be greater than 0' };
            }
            if (item.price <= 0) {
                return { valid: false, error: 'Item price must be greater than 0' };
            }
        }

        return { valid: true, error: null };
    }
};